import React,{ useState, useEffect, useMemo, useRef } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import colors from '../../../../../config/colors';
import I18n from '../../../../../components/shared/i18n/I18n.component';
import ImageIcon from '../../../../../components/shared/Icon/ImageIcon.component';
import TableContainer from '../../../../../components/web/ReactTable/Table.container';
import { ResponsivePie } from '@nivo/pie';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles({
	customFooterRow: {
		backgroundColor: `${colors.secondaryLight} !important`,
	},
	customFooterCell: {
		backgroundColor: `${colors.secondaryLight} !important`,
		fontWeight: 700,
	},
});

const DataSummaryPanel = (props: any) => {
	const tableHeaders = props.tableHeaders;
	//const tableData = props.tableData;
	const pieData = props.pieData;
	const navigation = props.navigation;
	const tableClickable = props.tableClickable;
	const footer = props.footer || false;
	const excelExportHandler = props.excelExportHandler;

	const [tableData, setTableData] = useState(props.tableData);
	// const [activeSort, setActiveSort] = useState({ name: '', direction: '' });
	const [sortDirection, setSortDirection] = useState('ASC');
	const [sortField, setSortField] = useState();

	useEffect(() => {
		setTableData(props.tableData);
	}, [props.tableData]);

	const [hoveredId, setHoveredId] = useState<string | null>(null);
	const [customTooltipData, setCustomTooltipData] = useState<any>(null);
	const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
	const [containerDimensions, setContainerDimensions] = useState<{ width: number; height: number } | null>(null);

	const pieContainerRef = useRef<any>(null);
	const classes = useStyles();

	/**
	 * Track the actual dimensions of the pie container using ResizeObserver
	 * This ensures tooltip positioning adapts to screen size changes
	 */
	useEffect(() => {
		const container = pieContainerRef.current;
		if (!container) return;

		const updateDimensions = () => {
			const rect = container.getBoundingClientRect();
			setContainerDimensions({ width: rect.width, height: rect.height });
		};

		// Initial measurement
		updateDimensions();

		// Watch for resize events
		const resizeObserver = new ResizeObserver(updateDimensions);
		resizeObserver.observe(container);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	/**
	 * Calculate tooltip position to display at the correct arc location in the pie chart
	 * when hovering a table row. This effect runs whenever the hovered row or container size changes.
	 * 
	 * The calculation converts the pie slice's position from polar coordinates (angle + radius)
	 * to Cartesian coordinates (x, y pixels) for CSS positioning that adapts to screen size.
	 */
	useEffect(() => {
		// Early return if no row is hovered, data is missing, or container dimensions not yet measured
		if (!hoveredId || !customTooltipData || !pieData?.length || !containerDimensions) {
			setTooltipPosition(null);
			return;
		}

		// Step 1: Calculate the total value of all pie slices
		// This represents the full 360° circle (2π radians)
		const total = pieData.reduce((sum: number, item: any) => sum + item.value, 0);
		
		// Step 2: Track the starting angle as we iterate through slices
		// Pie charts start at the top (12 o'clock position) and go clockwise
		let startAngle = 0;

		// Step 3: Iterate through each pie slice to find the hovered one
		for (const item of pieData) {
			// Calculate this slice's angular size based on its proportion of the total
			// Formula: (slice_value / total_value) × 2π
			// Example: if this slice is 25% of total, it gets 0.25 × 2π = π/2 radians (90°)
			const angle = (item.value / total) * 2 * Math.PI;
			
			// Calculate where this slice ends (next slice will start here)
			const endAngle = startAngle + angle;

			// Check if this is the slice that corresponds to the hovered table row
			if (item.id === hoveredId) {
				// Step 4: Find the center angle of this arc (middle of the slice)
				// We want the tooltip centered on the slice, not at its edges
				const centerAngle = (startAngle + endAngle) / 2;
				
				// Step 5: Calculate the effective chart radius based on actual container dimensions
				// ResponsivePie has 80px margins on all sides (top, right, bottom, left)
				// The actual chart area is the container minus these margins
				const marginPx = 80;
				const effectiveWidth = containerDimensions.width - (marginPx * 2);
				const effectiveHeight = containerDimensions.height - (marginPx * 2);
				// Use the smaller dimension to ensure the circle fits
				const chartRadius = Math.min(effectiveWidth, effectiveHeight) / 2;
				
				// Step 6: Determine the radial distance for tooltip placement
				// The pie chart has innerRadius=0.7 (donut hole) and outerRadius=1.0 (outer edge)
				// We position at 0.85, which is 85% from center, nicely between inner and outer edges
				const radiusRatio = 0.85; // normalized radius value
				const tooltipRadius = chartRadius * radiusRatio;
				
				// Step 7: Convert from polar coordinates to Cartesian (x, y) coordinates
				// Polar: (angle, radius) → Cartesian: (x, y)
				
				// Why subtract π/2 from angle?
				// - Standard trigonometry: 0° is at 3 o'clock (right), increases counter-clockwise
				// - Pie charts: 0° is at 12 o'clock (top), increases clockwise
				// - Subtracting π/2 (90°) rotates our coordinate system to match the pie chart
				
				// Calculate X position (horizontal) in pixels:
				// - Start at container center (width / 2)
				// - Add cosine of adjusted angle × tooltip radius
				// - This gives us the pixel position from the left edge of the container
				const xPixels = (containerDimensions.width / 2) + Math.cos(centerAngle - Math.PI / 2) * tooltipRadius;
				
				// Calculate Y position (vertical) in pixels:
				// - Start at container center (height / 2)
				// - Add sine of adjusted angle × tooltip radius
				// - This gives us the pixel position from the top edge of the container
				const yPixels = (containerDimensions.height / 2) + Math.sin(centerAngle - Math.PI / 2) * tooltipRadius;
				
				// Convert pixels to percentages for CSS positioning
				// This makes the tooltip position relative to the container, working at any size
				const x = (xPixels / containerDimensions.width) * 100;
				const y = (yPixels / containerDimensions.height) * 100;
				
				// Set the calculated position (in percentage units for CSS)
				setTooltipPosition({ x, y });
				break; // Found our slice, stop iterating
			}

			// Move to the next slice: this slice's end becomes the next slice's start
			startAngle = endAngle;
		}
	}, [hoveredId, customTooltipData, pieData, containerDimensions]);

	const sortedData = useMemo(() => {
		if (!tableData?.length) {
			return [];
		}
		if (!sortField) {
			return tableData;
		}
		return [...tableData].sort((a, b) => {
			const aType: any = typeof a[sortField];
			if (aType === 'number') {
				const result = a[sortField] - b[sortField];
				return sortDirection === 'ASC' ? result : -result;
			} else if (aType === 'string') {
				const result = a[sortField].localeCompare(b[sortField]);
				return sortDirection === 'ASC' ? result : -result;
			} else if (aType instanceof Date) {
				const result = a[sortField].getTime() - b[sortField].getTime();
				return sortDirection === 'ASC' ? result : -result;
			}
			return 0;
		});
	}, [tableData, sortDirection, sortField]);

	const handleSort = (field: any, direction: any) => {
		setSortDirection(direction);
		setSortField(field);
	};

	const handleRowClick = (rowData: any) => {
		if (!tableClickable) return;
		sessionStorage.setItem(
			'workingTimeReportDetailData',
			JSON.stringify({
				id: rowData.id,
				data: rowData,
			}),
		);

		navigation.navigate('workingTimeReportDetail', {
			id: rowData.id,
			data: rowData,
		});
	};

	return (
		<View style={styles.wrapper}>
			<View style={styles.titleAndExcel}>
				<I18n size={20} weight='normal'>
					{props?.title}
				</I18n>

				<Pressable
					onPress={() => {
						excelExportHandler?.();
						console.log('Export to Excel clicked');
					}}
					style={({ hovered }) => [
						styles.exportButton,
						hovered && styles.exportButtonHover,
					]}
				>
					<ImageIcon name={'tableExcel'} height={18} width={18} />
					<I18n weight='400' size={15} color={colors.titlePrimary}>
						{`exportToExcel`}
					</I18n>
				</Pressable>
			</View>

			<View style={styles.tableAndChartContainer}>
				<View
					style={{
						flex: 2,
						backgroundColor: '#fff',
						borderRadius: 1,
						padding: 0,
						minHeight: '100%',
					}}
				>
					<TableContainer
						headers={tableHeaders}
						data={sortedData}
						keyName={'id' + Math.floor(Math.random() * 1000000)}
						getNextData={() => {}}
						onSort={handleSort}
						onRowClick={handleRowClick}
						onRowMouseEnter={(rowData: any) => {
							console.log('row mouse enter', rowData);
							setHoveredId(rowData.id);
							// Find the corresponding pie data
							const pieItem = pieData.find(
								(item: any) => item.id === rowData.id,
							);
							if (pieItem) {
								setCustomTooltipData(pieItem);
							}
						}}
						onRowMouseLeave={() => {
							setHoveredId(null);
							setCustomTooltipData(null);
						}}
						//allDataFetched={allFetched}
						hasFooter={footer}
						windowScale={1}
						getUpdatedDataFromTable={() => {}}
						// outerClasses={{
						// 	footerRowRoot: classes.customFooterRow,
						// 	footerCellRoot: classes.customFooterCell,
						// }}
						//defaultExpandedRows={{ '0': true }}
						//showExpandHeader={true}
					/>
				</View>

				<View style={styles.pieCard}>
					<I18n size={14} weight='500'>
						{props?.pieTitle}
					</I18n>
					<View style={styles.separator}></View>
					<View style={styles.pie} ref={pieContainerRef}>
						{customTooltipData && tooltipPosition && (
							<div
								style={{
									position: 'absolute',
									left: `${tooltipPosition.x}%`,
									top: `${tooltipPosition.y}%`,
									transform: 'translate(-50%, -50%)',
									padding: '12px',
									background: 'white',
									border: '1px solid #ccc',
									borderRadius: 6,
									boxShadow: '2px 4px 2px 0 rgba(0,0,0,0.16)',
									zIndex: 1000,
									pointerEvents: 'none',
								}}
							>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										gap: 8,
									}}
								>
									<div
										style={{
											display: 'flex',
											flexDirection: 'row-reverse',
											alignItems: 'center',
										}}
									>
										<div
											style={{
												width: 8,
												height: 8,
												borderRadius: 100,
												backgroundColor: customTooltipData.color,
												marginLeft: 8,
											}}
										></div>
										<I18n size={12} weight='400' color={colors.darkGrey}>
											{customTooltipData.label}
										</I18n>
									</div>
									<I18n size={12} weight='400' color={colors.tooltipPercentage}>
										{customTooltipData.shownLabel}
									</I18n>
								</div>
							</div>
						)}
						<ResponsivePie
							data={pieData}
							colors={{ datum: 'data.color' }}
							onClick={datum => {
								console.log(datum);
								//console.log(pieData);
							}}
							onMouseEnter={datum => {
								setHoveredId(datum.id as string);
								setCustomTooltipData(datum.data);
							}}
							onMouseLeave={() => {
								setHoveredId(null);
								setCustomTooltipData(null);
							}}
							padAngle={0}
							cornerRadius={0}
							activeOuterRadiusOffset={8}
							activeId={hoveredId}
							borderWidth={0}
							isInteractive={false}
							innerRadius={0.7}
							enableArcLinkLabels={false}
							enableArcLabels={false}
							arcLabelsSkipAngle={10}
							animate={false}
							margin={{
								top: 80,
								bottom: 80,
								left: 80,
								right: 80,
							}}
						/>
					</View>
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		paddingVertical: 8,
		paddingHorizontal: 32,
		overflow: 'scroll',
	},
	wrapper: {
		gap: 24,
	},
	exportButton: {
		display: 'flex',
		flexDirection: 'row-reverse',
		paddingHorizontal: 16,
		paddingVertical: 1,
		borderRadius: 10,
		height: 38,
		gap: 10,
		justifyContent: 'center',
		alignItems: 'center',
	},
	exportButtonHover: {
		backgroundColor: colors.secondaryLight,
	},
	titleAndExcel: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	tableAndChartContainer: {
		width: '100%',
		height: 'calc(52px * 8 + 60px)',
		overflow: 'visible',
		display: 'flex',
		flexDirection: 'row-reverse',
		gap: 16,
	},
	pieCard: {
		flex: 1,
		backgroundColor: '#fff',
		borderRadius: 4,
		padding: 20,
	},
	pie: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		position: 'relative',
	},
	separator: {
		borderBottomColor: colors.lightGrey,
		borderBottomWidth: 1,
		paddingBottom: 18,
	},
});

export default DataSummaryPanel;
